import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  CheckCircle2, 
  XCircle, 
  Ban, 
  Circle, 
  Loader2,
  Bug,
  ChevronRight,
  ChevronLeft,
  Save
} from 'lucide-react';

export const TestRunExecutionPage = () => {
  const { testRunId } = useParams();
  const navigate = useNavigate();
  const [testRun, setTestRun] = useState(null);
  const [execResults, setExecResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Form state
  const [status, setStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [bugReference, setBugReference] = useState('');

  const loadTestRun = useCallback(async () => {
    try {
      const [runResponse, resultsResponse] = await Promise.all([
        api.get(`/testruns/${testRunId}`),
        api.get(`/testruns/${testRunId}/results`)
      ]);
      
      setTestRun(runResponse.data);
      
      // Sort results by tc_id
      const sorted = resultsResponse.data.sort((a, b) => {
        const aNum = parseInt(a.tc_id?.replace('TC', '') || '0');
        const bNum = parseInt(b.tc_id?.replace('TC', '') || '0');
        return aNum - bNum;
      });
      setExecResults(sorted);
      
      // Set initial form values for first item
      if (sorted.length > 0) {
        const first = sorted[0];
        setStatus(first.status || 'not_run');
        setNotes(first.notes || '');
        setBugReference(first.bug_reference || '');
      }
    } catch (error) {
      toast.error('Failed to load test run');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [testRunId]);

  useEffect(() => {
    loadTestRun();
  }, [loadTestRun]);

  // Update form when selected test case changes
  useEffect(() => {
    if (execResults.length > 0 && selectedIndex < execResults.length) {
      const current = execResults[selectedIndex];
      setStatus(current.status || 'not_run');
      setNotes(current.notes || '');
      setBugReference(current.bug_reference || '');
    }
  }, [selectedIndex, execResults]);

  const handleSave = async () => {
    if (!status) {
      toast.error('Please select a status');
      return;
    }
    
    const current = execResults[selectedIndex];
    setSaving(true);
    
    try {
      const response = await api.post(`/testruns/${testRunId}/execute`, {
        testcase_id: current.testcase_id,
        status,
        notes: notes || null,
        bug_reference: bugReference || null
      });
      
      // Update local state
      const updated = [...execResults];
      updated[selectedIndex] = {
        ...updated[selectedIndex],
        status,
        notes,
        bug_reference: bugReference,
        executed_at: new Date().toISOString()
      };
      setExecResults(updated);
      
      // Update test run summary
      if (response.data.summary) {
        setTestRun(prev => ({
          ...prev,
          ...response.data.summary
        }));
      }
      
      toast.success('Result saved');
    } catch (error) {
      toast.error('Failed to save result');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndNext = async () => {
    await handleSave();
    if (selectedIndex < execResults.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const goToPrevious = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedIndex < execResults.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };

  const getStatusIcon = (s) => {
    switch (s) {
      case 'pass':
        return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'fail':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'blocked':
        return <Ban className="h-4 w-4 text-yellow-600" />;
      default:
        return <Circle className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'pass':
        return <Badge className="bg-green-50 text-green-700 border-green-200">Pass</Badge>;
      case 'fail':
        return <Badge className="bg-red-50 text-red-700 border-red-200">Fail</Badge>;
      case 'blocked':
        return <Badge className="bg-yellow-50 text-yellow-700 border-yellow-200">Blocked</Badge>;
      default:
        return <Badge variant="outline" className="text-muted-foreground">Not Run</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-azure" />
        <span className="ml-3 text-muted-foreground">Loading test run...</span>
      </div>
    );
  }

  if (!testRun) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Test run not found</p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const currentResult = execResults[selectedIndex];
  const passRate = testRun.pass_percentage || 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="mb-6 gap-2" 
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />Back
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-blueprint tracking-tight mb-2">
            {testRun.name}
          </h1>
          <p className="text-muted-foreground">
            Test Execution - {testRun.total_cases || 0} test cases
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg">
                <Circle className="h-5 w-5 text-blueprint" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-blueprint">{testRun.total_cases || 0}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Total</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-green-700">{testRun.passed || 0}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Passed</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-red-700">{testRun.failed || 0}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Failed</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-50 rounded-lg">
                <Ban className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-yellow-700">{testRun.blocked || 0}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Blocked</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Circle className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <div className="text-2xl font-mono font-bold text-muted-foreground">{testRun.not_run || 0}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Not Run</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card className="p-4 mb-8">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-muted-foreground">Execution Progress</span>
            <span className="font-medium text-blueprint">{passRate}% pass rate</span>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div className="h-full flex">
              <div 
                className="bg-green-500 transition-all duration-300" 
                style={{ width: `${testRun.total_cases ? (testRun.passed / testRun.total_cases * 100) : 0}%` }} 
              />
              <div 
                className="bg-red-500 transition-all duration-300" 
                style={{ width: `${testRun.total_cases ? (testRun.failed / testRun.total_cases * 100) : 0}%` }} 
              />
              <div 
                className="bg-yellow-500 transition-all duration-300" 
                style={{ width: `${testRun.total_cases ? (testRun.blocked / testRun.total_cases * 100) : 0}%` }} 
              />
            </div>
          </div>
        </Card>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Test Case List */}
          <Card className="p-4 lg:col-span-1">
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Test Cases ({execResults.length})
            </h2>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {execResults.map((result, index) => (
                <div 
                  key={result.id} 
                  onClick={() => setSelectedIndex(index)}
                  className={`p-3 rounded-md cursor-pointer transition-all ${
                    selectedIndex === index 
                      ? 'bg-azure/10 border-2 border-azure' 
                      : 'bg-muted hover:bg-muted/80 border-2 border-transparent'
                  }`}
                  data-testid={`testcase-item-${result.tc_id}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono text-sm font-medium">{result.tc_id}</span>
                    {getStatusIcon(result.status)}
                  </div>
                  <p className="text-sm text-muted-foreground truncate">{result.scenario_title}</p>
                  {result.bug_reference && (
                    <div className="flex items-center gap-1 mt-1 text-xs text-red-600">
                      <Bug className="h-3 w-3" />
                      {result.bug_reference}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>

          {/* Execution Panel */}
          <Card className="p-6 lg:col-span-2">
            {currentResult ? (
              <div>
                {/* Test Case Header */}
                <div className="mb-6 pb-6 border-b border-border">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="px-3 py-1 bg-azure/10 text-azure rounded font-mono text-sm font-bold">
                        {currentResult.tc_id}
                      </span>
                      <span className="px-2 py-1 bg-muted rounded text-sm">
                        {currentResult.module}
                      </span>
                      {getStatusBadge(currentResult.status)}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {selectedIndex + 1} of {execResults.length}
                    </div>
                  </div>
                  <h2 className="text-xl font-heading font-semibold text-blueprint">
                    {currentResult.scenario_title}
                  </h2>
                </div>

                {/* Execution Form */}
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="status" className="text-sm font-medium">
                      Execution Status *
                    </Label>
                    <Select value={status} onValueChange={setStatus}>
                      <SelectTrigger className="mt-1.5" data-testid="execution-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_run">
                          <div className="flex items-center gap-2">
                            <Circle className="h-4 w-4 text-muted-foreground" />
                            Not Run
                          </div>
                        </SelectItem>
                        <SelectItem value="pass">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                            Pass
                          </div>
                        </SelectItem>
                        <SelectItem value="fail">
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            Fail
                          </div>
                        </SelectItem>
                        <SelectItem value="blocked">
                          <div className="flex items-center gap-2">
                            <Ban className="h-4 w-4 text-yellow-600" />
                            Blocked
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {status === 'fail' && (
                    <div>
                      <Label htmlFor="bugReference" className="text-sm font-medium flex items-center gap-2">
                        <Bug className="h-4 w-4 text-red-600" />
                        Bug Reference
                      </Label>
                      <Input
                        id="bugReference"
                        value={bugReference}
                        onChange={(e) => setBugReference(e.target.value)}
                        placeholder="e.g., JIRA-123, BUG-456"
                        className="mt-1.5"
                        data-testid="bug-reference-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="notes" className="text-sm font-medium">
                      Notes / Comments
                    </Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add observations, issues, or test notes..."
                      rows={4}
                      className="mt-1.5"
                      data-testid="execution-notes-input"
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-border">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToPrevious}
                        disabled={selectedIndex === 0}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={goToNext}
                        disabled={selectedIndex === execResults.length - 1}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-execution-button"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Save
                      </Button>
                      <Button
                        onClick={handleSaveAndNext}
                        disabled={saving || selectedIndex === execResults.length - 1}
                        className="bg-blueprint hover:bg-blueprint/90"
                        data-testid="save-next-button"
                      >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                        Save & Next
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-12">
                No test cases available
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};
